import React, { useEffect, useState } from 'react';
import {
  Box, Button, Card, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Typography, Alert, CircularProgress, Chip, IconButton, Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AssessmentIcon from '@mui/icons-material/Assessment';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { superAdminAPI } from '../../services/api';
import { PageHeader } from '../../components/ui/PageHeader';
import { getPageSubtitle } from '../../constants/pageSubtitles';
import { SuperAdminDataTable, AdminTableEditButton } from '../../components/superadmin/SuperAdminDataTable';
import { brandCss } from '../../theme/appTheme';
import { catchyPrimaryButtonSx } from '../../theme/formStyles';

const CORE_MODULES = [
  'trips', 'clients', 'vehicles', 'drivers', 'invoices', 'fuel', 'maintenance',
  'bilty', 'consignors', 'consignees', 'staff', 'helpers', 'branches', 'documents',
];

type ReportForm = {
  name: string;
  description: string;
  moduleKey: string;
  groupBy: string;
  chartType: string;
  dateField: string;
  status: string;
};

const defaultForm: ReportForm = {
  name: '',
  description: '',
  moduleKey: 'trips',
  groupBy: 'status',
  chartType: 'bar',
  dateField: 'createdAt',
  status: 'active',
};

export const SuperAdminReportTemplatesPage: React.FC = () => {
  const [reports, setReports] = useState<any[]>([]);
  const [crmModules, setCrmModules] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [open, setOpen] = useState(false);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [form, setForm] = useState<ReportForm>(defaultForm);
  const [saving, setSaving] = useState(false);

  const moduleOptions = [
    ...CORE_MODULES.map((k) => ({ key: k, label: k })),
    ...crmModules.map((m) => ({ key: m.key, label: m.label || m.name })),
  ];

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [reportsRes, crmRes] = await Promise.all([
        superAdminAPI.getReportTemplates(),
        superAdminAPI.getCrmModules().catch(() => ({ data: [] })),
      ]);
      setReports(reportsRes.data || []);
      setCrmModules(crmRes.data || []);
    } catch {
      setError('Failed to load report templates');
      setReports([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditingKey(null);
    setForm(defaultForm);
    setOpen(true);
  };

  const openEdit = (report: Record<string, unknown>) => {
    setEditingKey(String(report.key));
    setForm({
      name: String(report.name || ''),
      description: String(report.description || ''),
      moduleKey: String(report.moduleKey || 'trips'),
      groupBy: String(report.groupBy || 'status'),
      chartType: String(report.chartType || 'bar'),
      dateField: String(report.dateField || 'createdAt'),
      status: String(report.status || 'active'),
    });
    setOpen(true);
  };

  const closeDialog = () => {
    setOpen(false);
    setEditingKey(null);
    setForm(defaultForm);
  };

  const buildPayload = () => ({
    ...form,
    filters: [],
    aggregations: form.moduleKey === 'invoices'
      ? [{ field: 'totalAmount', fn: 'sum' }]
      : [],
  });

  const handleSave = async () => {
    if (!form.name.trim()) {
      setError('Report name is required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (editingKey) {
        await superAdminAPI.updateReportTemplate(editingKey, buildPayload());
        setMessage('Report template updated');
      } else {
        await superAdminAPI.createReportTemplate(buildPayload());
        setMessage('Report template created. Assign it to tenants under Report Access.');
      }
      closeDialog();
      load();
    } catch {
      setError(editingKey ? 'Failed to update report template' : 'Failed to create report template');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (key: string) => {
    if (!window.confirm('Delete this report template? Tenants will lose access.')) return;
    try {
      await superAdminAPI.deleteReportTemplate(key);
      setMessage('Report template deleted');
      load();
    } catch {
      setError('Failed to delete report');
    }
  };

  return (
    <Box>
      <PageHeader
        title="Report Templates"
        subtitle={getPageSubtitle('report_templates')}
        icon={<AssessmentIcon />}

        action={
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} sx={catchyPrimaryButtonSx}>
            Create Report
          </Button>
        }
      />

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {message && <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setMessage('')}>{message}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: brandCss.primary }} />
        </Box>
      ) : reports.length === 0 ? (
        <Card sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="textSecondary" gutterBottom>No report templates yet</Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Create trip, invoice, leads, or custom CRM reports for your tenants.
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} sx={catchyPrimaryButtonSx}>
            Create Your First Report
          </Button>
        </Card>
      ) : (
        <SuperAdminDataTable
          rows={reports}
          searchPlaceholder="Search reports..."
          recordLabel="reports"
          statusOptions={[{ label: 'All', value: 'all' }]}
          getStatus={() => 'active'}
          getRowId={(r) => r.key}
          columns={[
            { id: 'name', header: 'Report', searchValue: (r) => `${r.name} ${r.description || ''}`, exportValue: (r) => r.name, render: (r) => (
              <Box>
                <Typography fontWeight={700} fontSize={13}>{r.name}</Typography>
                {r.description && <Typography fontSize={11} color="text.secondary">{r.description}</Typography>}
              </Box>
            ) },
            { id: 'key', header: 'Key', searchValue: (r) => r.key, exportValue: (r) => r.key, render: (r) => (
              <Typography fontSize={12} color="text.secondary">{r.key}</Typography>
            ) },
            { id: 'module', header: 'Module', searchValue: (r) => r.moduleKey, exportValue: (r) => r.moduleKey, render: (r) => r.moduleKey },
            { id: 'chart', header: 'Chart', exportValue: (r) => r.chartType, render: (r) => (
              <Chip label={r.chartType || 'bar'} size="small" sx={{ bgcolor: brandCss.green100, color: brandCss.primaryDark, fontWeight: 600 }} />
            ) },
            { id: 'group', header: 'Group By', exportValue: (r) => r.groupBy, render: (r) => r.groupBy || '—' },
            { id: 'date', header: 'Date Field', exportValue: (r) => r.dateField, render: (r) => r.dateField || 'createdAt' },
            { id: 'status', header: 'Status', exportValue: (r) => r.status, filterType: 'select', filterOptions: [{ label: 'Active', value: 'active' }, { label: 'Hidden', value: 'hidden' }], filterValue: (r) => r.status || 'active', render: (r) => (
              <Chip label={r.status || 'active'} size="small" variant="outlined" sx={{ fontSize: 11, textTransform: 'capitalize' }} />
            ) },
          ]}
          exportFileName="super-admin-report-templates"
          exportSheetName="Reports"
          renderActions={(r) => (
            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
              <AdminTableEditButton onClick={() => openEdit(r)} title="Edit report" />
              <Tooltip title="Delete">
                <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); handleDelete(r.key); }}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
          emptyMessage="No report templates yet."
        />
      )}

      <Dialog open={open} onClose={closeDialog} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editingKey ? 'Edit Report Template' : 'Create Report Template'}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          {editingKey && (
            <TextField label="Report Key" value={editingKey} fullWidth size="small" disabled helperText="Key cannot be changed after creation" />
          )}
          <TextField label="Report Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} fullWidth size="small" required />
          <TextField label="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} fullWidth size="small" multiline rows={2} />
          <TextField select label="Module" value={form.moduleKey} onChange={(e) => setForm({ ...form, moduleKey: e.target.value })} fullWidth size="small">
            {moduleOptions.map((m) => <MenuItem key={m.key} value={m.key}>{m.label}</MenuItem>)}
          </TextField>
          <TextField label="Group By Field" value={form.groupBy} onChange={(e) => setForm({ ...form, groupBy: e.target.value })} fullWidth size="small" helperText="e.g. status, paymentStatus, vehicleNumber" />
          <TextField select label="Chart Type" value={form.chartType} onChange={(e) => setForm({ ...form, chartType: e.target.value })} fullWidth size="small">
            {['bar', 'line', 'pie', 'table'].map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </TextField>
          <TextField label="Date Field" value={form.dateField} onChange={(e) => setForm({ ...form, dateField: e.target.value })} fullWidth size="small" helperText="Used when tenant runs report with date range" />
          <TextField select label="Status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} fullWidth size="small">
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="hidden">Hidden</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeDialog} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving} sx={catchyPrimaryButtonSx}>
            {saving ? 'Saving…' : editingKey ? 'Save Changes' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
